"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { NotificationChannelView } from "@/hooks/notifications/use-notification-channels";

const BOTFATHER_URL = "https://t.me/BotFather";

interface TelegramConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: (name: string, botToken: string) => Promise<NotificationChannelView>;
  /** Called with the freshly-connected channel so the parent can open enrollment. */
  onConnected: (channel: NotificationChannelView) => void;
}

export function TelegramConnectDialog({
  open,
  onOpenChange,
  onConnect,
  onConnected,
}: TelegramConnectDialogProps) {
  const [name, setName] = useState("");
  const [botToken, setBotToken] = useState("");
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setBotToken("");
    }
  }, [open]);

  const canConnect = name.trim().length > 0 && botToken.trim().length > 0 && !connecting;

  const handleConnect = async () => {
    if (!canConnect) return;
    setConnecting(true);
    try {
      const channel = await onConnect(name.trim(), botToken.trim());
      onOpenChange(false);
      onConnected(channel);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to connect Telegram bot");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect a Telegram bot</DialogTitle>
          <DialogDescription>
            A dedicated Telegram bot delivers escalation alerts to your team. One bot serves one channel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ol className="list-decimal space-y-1.5 rounded-md bg-muted/50 py-3 pl-8 pr-4 text-sm text-muted-foreground">
            <li>
              <span>Open</span>{" "}
              <a
                href={BOTFATHER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                @BotFather
              </a>{" "}
              <span>in Telegram and send</span>{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/newbot</code><span>.</span>
            </li>
            <li>Follow the prompts to pick a name and username for your bot.</li>
            <li>
              <span>BotFather replies with an API</span> <strong>token</strong> <span>(like</span>{" "}
              <code className="font-mono text-xs">123456:ABC-DEF…</code><span>) — copy it.</span>
            </li>
            <li><span>Paste the token below and click</span> <strong>Connect</strong><span>.</span></li>
            <li>
              On the next step, scan the QR code or share the enrollment link so your team starts the
              bot — a chat only receives alerts after it has started the bot.
            </li>
          </ol>

          <div className="space-y-1.5">
            <Label htmlFor="nc-tg-name">Name</Label>
            <Input
              id="nc-tg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ops Telegram"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nc-tg-token">Bot token</Label>
            <Input
              id="nc-tg-token"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456:ABC-DEF…"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              <span>From</span>{" "}
              <a
                href={BOTFATHER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                @BotFather
              </a>
              <span>. Stored encrypted; never shown again.</span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={connecting}>
            Cancel
          </Button>
          <Button onClick={handleConnect} disabled={!canConnect}>
            {connecting ? "Connecting…" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
